import { debounce } from 'perfect-debounce';
import Vue from 'vue';
import {
    getEhsCode,
    getEhsHybridPlusCode,
    getMarvelRCode,
    getMg3Code,
    getMg4Code,
    getMg4Mce2026Code,
    getMg4UrbanEv2026Code,
    getMg5Code,
    getMgs5Code,
    getMgs6Code,
    getMgs9Code,
    getMgZsHevCode,
    getZsCode,
    getZsEvCode,
    normalizeModelName,
} from '../utils';

Vue.component('agricole-fr-calculator-component', {
    props: {
        product: {
            value: String,
        },
        modelDescription: {
            value: String,
        },
        configuratorResult: {
            value: Object,
        },
        agricoleScriptUrl: {
            value: String,
        },
    },
    data() {
        return {
            isMobileDetailsVisible: false,
            isScriptMounted: false,
            isModalOpen: false,
            widgetInitializedAt: null,
            monthlyInstallment: null,
            originalPrice: 0,
            calculatorResultForDod: null,
            dodDescription: null,
            footerLegalNotes: null,
            oneTrustCookies: {
                essential: false,
                performance: false,
                functional: false,
                targeting: false,
            },
        };
    },

    mounted() {
        this.mountagricoleScript();

        setTimeout(() => {
            this.handleSetOneTrustConsentCategories();
            this.getDefaultInstallment();
            this.originalPrice = this.$parent.agricoleOriginalPrice;
        }, 2000);
    },

    watch: {
        configuratorResult: {
            handler: debounce(function (newValues) {
                this.updateDefaultInstallment(newValues);
            }, 700),
            deep: true,
        },
        footerLegalNotes(newNotes) {
            if (!newNotes) return;
            EventBus.$emit('agricole-calculator-footer-notes', newNotes);
        },
    },

    computed: {
        mobileDetailsText() {
            return this.isMobileDetailsVisible
                ? 'agricole-fr-calculator__text--mobile--active'
                : '';
        },
        mobileDetailsContainer() {
            return this.isMobileDetailsVisible
                ? 'agricole-details__container--mobile--visible'
                : '';
        },
    },

    methods: {
        handleSetOneTrustConsentCategories() {
            const oneTrustActiveGroups = window.OnetrustActiveGroups;

            if (!oneTrustActiveGroups) return;

            const splitGroups = oneTrustActiveGroups.split(',');
            this.oneTrustCookies.essential = splitGroups.includes('C0001');
            this.oneTrustCookies.performance = splitGroups.includes('C0002');
            this.oneTrustCookies.functional = splitGroups.includes('C0003');
            this.oneTrustCookies.targeting = splitGroups.includes('C0004');
        },
        toggleMobileDetails() {
            this.isMobileDetailsVisible = !this.isMobileDetailsVisible;
        },
        closeModal() {
            const modalElement = document.getElementById(
                'agricole-fr-calculator-modal',
            );
            if (modalElement) {
                modalElement.classList.remove('active');
                modalElement.classList.add('inactive');
            }

            // Clean up the widget container
            const contentContainer = document.getElementById(
                'agricole-fr-calculator',
            );
            if (contentContainer) {
                contentContainer.innerHTML = '';
            }

            this.isModalOpen = false;
            this.widgetInitializedAt = null;
        },
        closeModalOnOverlayClick() {
            // Close modal when clicking on overlay (outside content)
            this.closeModal();
        },
        matchModelSlugWithName(modelSlug) {
            // FR Team asked that ehs is displayed as MG EHS PHEV
            // Because that's a special case, we're handling it here.
            // `normalizeModelName` is being used across multiple components,
            // so we're not changing it there so we don't break other components.
            switch (modelSlug) {
                case 'ehs':
                    return 'MG EHS PHEV';
                case 'mg3':
                    return 'MG3 Hybrid+';
                case 'ehs-hev':
                    return 'MG EHS Hybrid+';
            }
            return normalizeModelName(modelSlug);
        },
        mountagricoleScript() {
            const agricoleScript = document.createElement('script');
            agricoleScript.async = true;
            agricoleScript.src = this.agricoleScriptUrl;

            agricoleScript.onload = () => {
                // Wait a bit to ensure FCABFC3 is fully initialized
                const checkInterval = setInterval(() => {
                    if ('FCABFC3' in window) {
                        this.isScriptMounted = true;
                        clearInterval(checkInterval);
                    }
                }, 100);

                // Stop checking after 5 seconds
                setTimeout(() => clearInterval(checkInterval), 5000);
            };

            agricoleScript.onerror = () => {
                console.error('Failed to load Agricole script');
            };

            document.head.appendChild(agricoleScript);
        },

        getDefaultConfigurationObject(propCarEdition, propCarColor) {
            const carModel = this.getCarModel(this.$parent.model);
            const carEdition =
                propCarEdition || this.$parent.configuratorResult.type.key;
            const carColor =
                propCarColor || this.$parent.configuratorResult.color.key;

            return {
                targetElt: null,
                config: {
                    debug: false,
                    cookies: {
                        essential: this.oneTrustCookies.essential,
                        performance: this.oneTrustCookies.performance,
                        comfort: this.oneTrustCookies.functional,
                        advertising: this.oneTrustCookies.targeting,
                    },
                    marketCode: 'FR',
                    lang: 'fr',
                    touchpoint: 'mg-fr',
                    theme: 'mg',
                    closeBtn: true,
                    onClose: this.onCloseCallback,
                    onSelect: null,
                    vehicle: {
                        img: `https://cdn.mgmotor.eu/images/configurator/${carModel}/${carEdition}/${carColor}.png`,
                        objectType: '1',
                        brandCode: '6F',
                        brandDescription: 'MG',
                        modelCode: this.getModelCode(),
                        modelDescription: ' ',
                        versionCode: this.getVersionCode(),
                        versionDescription: `${this.product} - ${this.$parent.configuratorResult.type.name}: ${this.modelDescription}`,
                        condition: 'N',
                        fuelType: this.getFuelType(),
                        energyClass: '',
                        euroClass: '',
                        originalPrice: this.$parent.agricoleOriginalPrice,
                        ecommercePrice: this.$parent.agricoleEcommercePrice,
                        priceCurrency: 'EUR',
                        originalPriceNoVat: 0,
                        usePriceWithVat: true,
                    },
                },
            };
        },

        initAgricoleScript() {
            const isWidgetUnavailable =
                !('FCABFC3' in window) ||
                !this.isScriptMounted ||
                this.isModalOpen;

            if (isWidgetUnavailable) return;

            this.isModalOpen = true;

            // Show the modal
            const modalElement = document.getElementById(
                'agricole-fr-calculator-modal',
            );
            if (!modalElement) {
                this.isModalOpen = false;
                return;
            }

            modalElement.classList.remove('inactive');
            modalElement.classList.add('active');

            // Create a fresh container for the widget each time
            const contentContainer = document.getElementById(
                'agricole-fr-calculator',
            );

            if (!contentContainer) {
                this.isModalOpen = false;
                modalElement.classList.remove('active');
                modalElement.classList.add('inactive');
                return;
            }

            const widgetContainer = document.createElement('div');
            widgetContainer.id = 'agricole-widget-container';
            contentContainer.innerHTML = '';
            contentContainer.appendChild(widgetContainer);

            // Wait for DOM to be ready and then initialize
            setTimeout(() => {
                try {
                    const initObject = this.getDefaultConfigurationObject();
                    // Use the fresh widget container
                    initObject.targetElt = widgetContainer;
                    window.FCABFC3.init(initObject);
                    this.widgetInitializedAt = Date.now();

                    const observer = new MutationObserver(() => {
                        if (widgetContainer.childElementCount === 0) {
                            observer.disconnect();
                            widgetContainer.remove();
                            this.isModalOpen = false;
                            modalElement.classList.remove('active');
                            modalElement.classList.add('inactive');
                        }
                    });
                    observer.observe(widgetContainer, { childList: true });
                } catch (error) {
                    // Reset state on error
                    this.closeModal();
                }
            }, 300);
        },
        getDefaultInstallment() {
            if (!('FCABFC3' in window)) return;

            const defaultInstalmentObject =
                this.getDefaultConfigurationObject();
            window.FCABFC3.defaultInstalment(defaultInstalmentObject);
        },

        updateDefaultInstallment(configuratorResultValues) {
            this.monthlyInstallment = null;
            if (!('FCABFC3' in window)) return;

            const carEdition = configuratorResultValues.type.key;
            const carColor = configuratorResultValues.color.key;
            const updateObject = this.getDefaultConfigurationObject(
                carEdition,
                carColor,
            );

            updateObject.config.vehicle = {
                ...updateObject.config.vehicle,
                optionalTotalValue:
                    this.$parent.agricoleOriginalPrice - this.originalPrice,
                optionalTotalValueNoVat: 0,
            };

            window.FCABFC3.update(updateObject);
        },

        // Calculate how long the widget has been open
        onCloseCallback(payload) {
            // Calculate how long the widget has been open
            const widgetOpenDuration = this.widgetInitializedAt
                ? Date.now() - this.widgetInitializedAt
                : 0;

            // Always update monthlyInstallment for button visibility
            if (payload && payload.instalment) {
                this.monthlyInstallment =
                    payload.instalment?.monthlyInstalment ?? null;
            }

            // If callback happens within 100 milliseconds of opening, it's auto-callback
            // Just update the data but don't close the modal
            if (widgetOpenDuration < 100) {
                return;
            }

            // User closed widget (either with submission or X button)
            // Check if it was an actual submission
            const isActualSubmission =
                payload &&
                (payload.applyForFinance === true ||
                    payload.configId !== undefined);

            if (isActualSubmission && payload.instalment) {
                this.calculatorResultForDod = {
                    modelName: `${this.matchModelSlugWithName(
                        this.$parent.model,
                    )} ${this.configuratorResult.type.name}`,
                    discountedPrice: `${this.$parent.agricoleEcommercePrice}`,
                    monthlyInstallment:
                        payload.instalment?.monthlyInstalment ?? null,
                    firstInstallmentAmount: this.getPayloadDetailValueByKey(
                        payload.instalment.detailsTable,
                        'firstInstallmentAmount',
                    ),
                    durationInMonths: payload.instalment?.duration ?? null,
                    mileage: this.getPayloadDetailValueByKey(
                        payload.instalment.detailsTable,
                        'mileage',
                    ),
                };

                this.footerLegalNotes = this.getPayloadDetailValueByKey(
                    payload.instalment.detailsTable,
                    'parsedLegalNote',
                );

                this.dodDescription = this.getDodSFDescription(payload);
            }
        },

        getPayloadDetailValueByKey(payloadDetails, key) {
            if (!payloadDetails) return null;
            return (
                payloadDetails
                    .filter((detail) => detail.key === key)[0]
                    ?.value.toString() ?? null
            );
        },

        getPayloadDetailByKey(payloadDetails, key) {
            if (!payloadDetails) return null;
            return (
                payloadDetails.filter((detail) => detail.key === key)[0] ?? null
            );
        },

        getCarModel(modelSlug) {
            switch (modelSlug) {
                case 'mg4':
                    return 'mg4-2023';
                case 'zs-ev':
                    return 'mg-zs-ev';
                case 'zs':
                    return 'mg-zs-petrol';
                case 'ehs':
                    return 'mg-ehs';
                case 'marvel-r':
                    return 'mg-marvel-r';
                case 'ehs-hev':
                    return 'hs-hev-2025';
                default:
                    return modelSlug;
            }
        },

        getVersionCode() {
            const configuratorVehicle = this.$parent.configuratorResult;
            switch (this.product) {
                case 'EHS':
                    return getEhsCode(configuratorVehicle);
                case 'EHS Hybrid+':
                    return getEhsHybridPlusCode(configuratorVehicle);
                case 'MARVEL R':
                    return getMarvelRCode(configuratorVehicle);
                case 'MG3':
                    return getMg3Code(configuratorVehicle);
                case 'MG4':
                    return getMg4Code(configuratorVehicle);
                case 'MG4 MCE':
                    return getMg4Mce2026Code(configuratorVehicle);
                case 'MG4 URBAN EV':
                    return getMg4UrbanEv2026Code(configuratorVehicle);
                case 'MG4 Urban':
                    return getMg4UrbanEv2026Code(configuratorVehicle);
                case 'MG5':
                    return getMg5Code(configuratorVehicle);
                case 'MGS5':
                    return getMgs5Code(configuratorVehicle);
                case 'MGS6 EV':
                    return getMgs6Code(configuratorVehicle);
                case 'MG ZS Hybrid+':
                    return getMgZsHevCode(configuratorVehicle);
                case 'ZS':
                    return getZsCode(configuratorVehicle);
                case 'ZS EV':
                    return getZsEvCode(configuratorVehicle);
                case 'MG4 EV':
                    return getMg4Mce2026Code(configuratorVehicle);
                case 'MGS9 PHEV':
                    return getMgs9Code(configuratorVehicle);
                default:
                    return '';
            }
        },

        getModelCode() {
            const versionCode = this.getVersionCode();
            if (versionCode.length < 4) return '';
            return versionCode.substring(0, 4);
        },

        getFuelType() {
            switch (this.product) {
                case 'HS':
                case 'ZS':
                    return '2';
                case 'EHS':
                    return '9';
                default:
                    return '4';
            }
        },

        finishCalculator() {
            this.$parent.completeConfigurator();
            const calculatorResultForDod = {
                isAgricoleCalculation: true,
                dodDescription: this.dodDescription,
            };
            EventBus.$emit(
                'agricole-calculator-finished',
                calculatorResultForDod,
            );
            EventBus.$emit(
                'agricole-calculator-leasing-finished',
                this.monthlyInstallment,
            );
            EventBus.$emit('toggle-agricole-calculator-footer-notes', true);
        },

        formatAsPrice(price) {
            return `€ ${price.toLocaleString('nl-NL', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })}`;
        },

        getDetailsOfSimulation(payload) {
            const downPayment = this.getPayloadDetailValueByKey(
                payload.instalment.detailsTable,
                'downPayment',
            );

            const formattedMonthlyInstallment = this.formatAsPrice(
                Number(payload.instalment?.monthlyInstalment) ?? 0,
            );

            const formattedDownPayment = this.formatAsPrice(
                parseFloat(downPayment),
            );

            const restOfDuration = payload.instalment.duration - 1;

            return `Valable dans le réseau MG participant en France Métropolitaine uniquement pour les particuliers. Location Longue durée sur ${payload.instalment.duration} mois. Premier loyer majoré de ${formattedDownPayment} * et ${restOfDuration} loyers de ${formattedMonthlyInstallment} *(1)`;
        },

        getDodSFDescription(payload) {
            const hasIziOption = Object.keys(
                this.configuratorResult.accessoriesOptions,
            ).includes('izi');

            const modelName = this.calculatorResultForDod.modelName;
            const modelColor = this.configuratorResult.color.name;
            const simulationDetails = this.getDetailsOfSimulation(payload);

            const selectedPromotions = Object.values(
                this.configuratorResult.promotions,
            ).filter((opt) => opt.selected);

            let promotionDiscountsStr = '';

            if (Boolean(selectedPromotions.length)) {
                selectedPromotions.forEach((promotion, index) => {
                    const promotionDiscount =
                        promotion.shopPriceImpactMetallic ??
                        promotion.shopPriceImpactNonMetallic;

                    const formattedDiscount = this.formatAsPrice(
                        Number(promotionDiscount.replace('-', '')),
                    );

                    promotionDiscountsStr +=
                        index === 0
                            ? ` ${formattedDiscount} ${promotion.name}`
                            : `, ${formattedDiscount} ${promotion.name}`;
                });
            }

            const discountedPrice = this.formatAsPrice(
                Number(this.calculatorResultForDod.discountedPrice),
            );

            let sfDescription = '';
            switch (true) {
                case this.configuratorResult.price.key === 'lease' &&
                    hasIziOption:
                    sfDescription = `Lead ayant configuré un/une ${modelName} ${modelColor} intéressé par la borne de recharge Izy by EDF. Configuration avec le type de financement suivant : LLD pour un total de ${discountedPrice} / TTC par mois, ${simulationDetails}.`;
                    break;
                case this.configuratorResult.price.key === 'cash' &&
                    hasIziOption:
                    sfDescription = `Lead ayant configuré un/une ${modelName} ${modelColor} intéressé par la borne de recharge Izy by EDF. Configuration avec le type de financement suivant : Comptant, avec les remises suivantes${promotionDiscountsStr}.`;
                    break;
                default:
                    sfDescription = `Lead ayant configuré un/une ${modelName}, au prix remisé de ${this.calculatorResultForDod.discountedPrice} avec une offre de financement en LLD sur le site, avec les caractéristiques suivantes : ${this.calculatorResultForDod.monthlyInstallment}€ par mois, avec un premier apport de ${this.calculatorResultForDod.firstInstallmentAmount}€, d'une durée de ${this.calculatorResultForDod.durationInMonths} mois et un kilométrage de ${this.calculatorResultForDod.mileage} km.`;
            }

            return sfDescription;
        },
    },
});
